import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getOverdueByPhase,
  getOverdueByPhaseExport,
  getOverdueSla,
} from "../../../../actions/labOpsAnalytics";
import useAbortController from "../../../Common/hooks/useAbortController";
import { formatAntdDateRange } from "../../../../utils/helpers";
import { getDefaultLastOneMonthDateRange, overdueFilterCatalogs, overdueFilterFields } from "../utils/utils";
import {
  buildCategoryLabelById,
  buildOverdueByPhaseParams,
  buildOverdueDrillDownExportParams,
  buildOverdueExportFileName,
  buildOverdueExportRows,
  buildOverdueRowExportParams,
  buildOverdueSlaParams,
  buildReasonLabelById,
  DEFAULT_SLA_AGGREGATE,
  downloadOverdueExportExcel,
  flattenOverdueByPhaseResult,
  OTHER_CHART_SLICE_KEY,
  SLA_OVERALL_DATA_KEY,
  parseChartSeriesNumericId,
  toOptionalIntegerIds,
} from "../utils/overdueUtils";
import { buildOverdueRowHeaderItems } from "../DataOpsSampleModalHeader";
import DataOpsFilters from "../DataOpsFilters";
import OverdueTable from "../tables/OverdueTable";
import OverdueCharts from "../charts/OverdueCharts";
import OverdueDrillDownModal from "../OverdueDrillDownModal";
import "../DataOpsAnalytics.css";

const createDefaultOverdueFilters = () => ({
  dateRange: getDefaultLastOneMonthDateRange(),
  testCategoryId: [],
  testId: [],
  customerId: "",
  search: "",
  reasonId: "",
  retest: false,
  subscriptionOnly: false,
});

/**
 * Overdue tab — filters + charts + table + Excel export + API-scoped drill-down.
 */
export default function OverdueTab() {
  const isMounted = useRef(true);
  const {
    setNewAbortController: setPhaseAbortController,
    clearAbortController: clearPhaseAbortController,
  } = useAbortController();
  const {
    setNewAbortController: setSlaAbortController,
    clearAbortController: clearSlaAbortController,
  } = useAbortController();
  const {
    setNewAbortController: setDrillDownAbortController,
    clearAbortController: clearDrillDownAbortController,
  } = useAbortController();
  const {
    setNewAbortController: setExportAbortController,
    clearAbortController: clearExportAbortController,
  } = useAbortController();

  const [filters, setFilters] = useState(createDefaultOverdueFilters);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const [slaAggregate, setSlaAggregate] = useState(DEFAULT_SLA_AGGREGATE);
  const [slaSeries, setSlaSeries] = useState([]);
  const [slaLoading, setSlaLoading] = useState(false);
  const [slaErrorMessage, setSlaErrorMessage] = useState(null);

  const reasonLabelById = buildReasonLabelById(reasonOptions);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setDrillDown(null);
    clearDrillDownAbortController();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchOverdueRows = async () => {
      const params = buildOverdueByPhaseParams(filters, formatAntdDateRange);
      if (!params) {
        setRows([]);
        return;
      }

      setLoading(true);
      const abortController = setPhaseAbortController();
      const response = await getOverdueByPhase(params, abortController.signal);

      if (!isMounted.current) {
        return;
      }
      if (response.canceled) {
        setLoading(false);
        clearPhaseAbortController();
        return;
      }

      setRows(response.success ? flattenOverdueByPhaseResult(response.result) : []);
      if (!response.success) {
        toast.error(response.message || "Failed to load overdue tests.");
      }
      setLoading(false);
      clearPhaseAbortController();
    };

    fetchOverdueRows();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchOverdueSla = async () => {
      const baseParams = buildOverdueSlaParams(filters, slaAggregate, formatAntdDateRange);
      if (!baseParams) {
        setSlaSeries([]);
        setSlaErrorMessage(null);
        return;
      }

      // One abort controller for the whole batch — parallel category calls must share
      // the same signal so siblings are not cancelled by setNewAbortController().
      const abortController = setSlaAbortController();
      const paramsWithoutCategories = { ...baseParams };
      delete paramsWithoutCategories.test_category_id;

      const selectedCategoryIds = toOptionalIntegerIds(filters.testCategoryId);
      const categoryNamesById = buildCategoryLabelById(categoryOptions);

      const seriesRequests = selectedCategoryIds.length === 0
        ? [{
          seriesKey: SLA_OVERALL_DATA_KEY,
          legendName: "All categories",
          params: paramsWithoutCategories,
        }]
        : selectedCategoryIds.map((categoryId) => ({
          seriesKey: `id:${categoryId}`,
          legendName: categoryNamesById.get(categoryId) || String(categoryId),
          params: {
            ...paramsWithoutCategories,
            test_category_id: [categoryId],
          },
        }));

      setSlaLoading(true);
      setSlaErrorMessage(null);

      const responses = await Promise.all(
        seriesRequests.map((request) => (
          getOverdueSla(request.params, abortController.signal)
        )),
      );

      if (!isMounted.current) {
        return;
      }
      if (responses.some((response) => response.canceled)) {
        setSlaLoading(false);
        clearSlaAbortController();
        return;
      }

      const failedResponse = responses.find((response) => !response.success);
      if (failedResponse) {
        setSlaSeries([]);
        setSlaErrorMessage(failedResponse.message || "Failed to load SLA trend.");
        toast.error(failedResponse.message || "Failed to load SLA trend.");
        setSlaLoading(false);
        clearSlaAbortController();
        return;
      }

      setSlaSeries(
        seriesRequests.map((request, index) => ({
          seriesKey: request.seriesKey,
          legendName: request.legendName,
          periods: Array.isArray(responses[index].result) ? responses[index].result : [],
        })),
      );
      setSlaLoading(false);
      clearSlaAbortController();
    };

    fetchOverdueSla();
  }, [filters, slaAggregate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Catalog may load after the first SLA fetch — refresh legend names without refetching.
  useEffect(() => {
    if (!categoryOptions.length) {
      return;
    }
    const categoryNamesById = buildCategoryLabelById(categoryOptions);
    setSlaSeries((previousSeries) => {
      if (!previousSeries.length) {
        return previousSeries;
      }
      let didChange = false;
      const nextSeries = previousSeries.map((series) => {
        if (series.seriesKey === SLA_OVERALL_DATA_KEY) {
          return series;
        }
        const categoryId = parseChartSeriesNumericId(series.seriesKey);
        if (categoryId === "") {
          return series;
        }
        const nextLegendName = categoryNamesById.get(categoryId);
        if (!nextLegendName || nextLegendName === series.legendName) {
          return series;
        }
        didChange = true;
        return { ...series, legendName: nextLegendName };
      });
      return didChange ? nextSeries : previousSeries;
    });
  }, [categoryOptions]);

  const openExportScopedDrillDown = async ({
    title,
    subtitle,
    headerItems = null,
    params,
  }) => {
    if (!params) {
      toast.warn("Unable to open this breakdown for the current selection.");
      return;
    }

    setDrillDown({
      title,
      subtitle,
      headerItems,
      rows: [],
      loading: true,
    });
    const abortController = setDrillDownAbortController();
    const response = await getOverdueByPhaseExport(params, abortController.signal);

    if (!isMounted.current) {
      return;
    }
    if (response.canceled) {
      setDrillDown(null);
      clearDrillDownAbortController();
      return;
    }
    if (!response.success) {
      setDrillDown(null);
      clearDrillDownAbortController();
      toast.error(response.message || "Failed to load overdue breakdown.");
      return;
    }

    setDrillDown({
      title,
      subtitle,
      headerItems,
      rows: flattenOverdueByPhaseResult(response.result),
      loading: false,
    });
    clearDrillDownAbortController();
  };

  const handleExportTableData = async () => {
    if (exportLoading) {
      return;
    }

    const params = buildOverdueByPhaseParams(filters, formatAntdDateRange);
    if (!params) {
      toast.warn("Select a valid date range before exporting.");
      return;
    }

    setExportLoading(true);
    const abortController = setExportAbortController();
    const response = await getOverdueByPhaseExport(params, abortController.signal);

    if (!isMounted.current) {
      return;
    }
    if (response.canceled) {
      setExportLoading(false);
      clearExportAbortController();
      return;
    }
    if (!response.success) {
      setExportLoading(false);
      clearExportAbortController();
      toast.error(response.message || "Failed to export overdue data.");
      return;
    }

    const exportRows = buildOverdueExportRows(flattenOverdueByPhaseResult(response.result));
    setExportLoading(false);
    clearExportAbortController();

    if (!exportRows.length) {
      toast.warn("No data available to export for the current filters.");
      return;
    }
    downloadOverdueExportExcel(exportRows, buildOverdueExportFileName());
    toast.success("Overdue data exported successfully.");
  };

  const handleChartDrillDown = (selection) => {
    if (!selection) {
      return;
    }
    if (selection.seriesKey === OTHER_CHART_SLICE_KEY) {
      toast.info("Open a named category or reason to view the breakdown.");
      return;
    }
    openExportScopedDrillDown({
      title: selection.title,
      subtitle: selection.subtitle,
      params: buildOverdueDrillDownExportParams(filters, selection, formatAntdDateRange),
    });
  };

  const handleViewRow = (record) => {
    if (!record) {
      return;
    }
    openExportScopedDrillDown({
      headerItems: buildOverdueRowHeaderItems(record, reasonLabelById),
      params: buildOverdueRowExportParams(filters, record, formatAntdDateRange),
    });
  };

  return (
    <div className="DataOpsAnalytics">
      <DataOpsFilters
        idPrefix="overdue"
        fields={overdueFilterFields}
        catalogs={overdueFilterCatalogs}
        filters={filters}
        onFiltersChange={setFilters}
        onReasonOptionsChange={setReasonOptions}
        onCategoryOptionsChange={setCategoryOptions}
        onExport={handleExportTableData}
        exportLoading={exportLoading}
        exportDisabled={loading || rows.length === 0}
      />

      <div className="DataOpsAnalytics__StackedLayout">
        <div className="DataOpsAnalytics__ChartsPane">
          <OverdueCharts
            rows={rows}
            loading={loading}
            reasonLabelById={reasonLabelById}
            slaSeries={slaSeries}
            slaLoading={slaLoading}
            slaErrorMessage={slaErrorMessage}
            slaAggregate={slaAggregate}
            onSlaAggregateChange={setSlaAggregate}
            onDrillDown={handleChartDrillDown}
          />
        </div>
        <div className="DataOpsAnalytics__TablePane DataOpsAnalytics__TablePane--wide">
          <OverdueTable
            rows={rows}
            loading={loading}
            reasonLabelById={reasonLabelById}
            onViewRow={handleViewRow}
          />
        </div>
      </div>

      <OverdueDrillDownModal
        open={Boolean(drillDown)}
        title={drillDown?.title}
        subtitle={drillDown?.subtitle}
        headerItems={drillDown?.headerItems}
        rows={drillDown?.rows}
        loading={Boolean(drillDown?.loading)}
        reasonLabelById={reasonLabelById}
        onClose={() => {
          clearDrillDownAbortController();
          setDrillDown(null);
        }}
      />
    </div>
  );
}
